import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { superAdminApi } from '../api/superAdminApi';
import { useAuth } from './useAuth';

/**
 * SUPER_ADMIN s `?unitId=X` v URL pracuje v kontextu konkretni jednotky.
 * UNIT_ADMIN/RECORDER: unitId z usera, parametr je ignorovan.
 */
export const useImpersonation = () => {
  const { user } = useAuth();
  const location = useLocation();

  const rawUnitId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('unitId');
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [location.search]);

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const effectiveUnitId = isSuperAdmin ? rawUnitId : user?.unitId ?? null;
  const isImpersonating = isSuperAdmin && rawUnitId !== null;

  const unitQuery = useQuery({
    queryKey: ['superadmin', 'units'],
    queryFn: superAdminApi.listUnits,
    enabled: isSuperAdmin,
    staleTime: 60_000,
  });

  const impersonatedUnit = isImpersonating
    ? unitQuery.data?.find((u) => u.id === rawUnitId) ?? null
    : null;

  return {
    isImpersonating,
    impersonatedUnit,
    effectiveUnitId,
    units: unitQuery.data ?? [],
    unitsLoading: unitQuery.isLoading,
  };
};

/** Prida `?unitId=X` k admin linku pokud SA impersonuje. */
export const withUnit = (path: string, unitId: number | null): string => {
  if (unitId === null) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}unitId=${unitId}`;
};
